import React, { useMemo, useState } from 'react';
import charityData from '../constants/data-charity';
import CharityDetailModal from '../components/common/CharityDetailModal';

const CharityPage = () => {
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter and sort charity data
  const filteredCharityData = useMemo(() => {
    return charityData.filter((item) => item.enable === true).sort((a, b) => a.top - b.top);
  }, []);

  const handleViewMore = (charity) => {
    setSelectedCharity(charity);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCharity(null);
  };

  const formatAmount = (amount) => {
    if (!amount) return '0 VND';
    return new Intl.NumberFormat('vi-VN').format(amount) + ' VND';
  };

  const formatCount = (count) => {
    return new Intl.NumberFormat('vi-VN').format(count);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#fe6601] mb-4">
            QUỸ THIỆN NGUYỆN GK88
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Khám phá các chương trình thiện nguyện đang được triển khai và cùng chúng tôi lan tỏa
            yêu thương đến cộng đồng
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Chương trình đang triển khai và Tổng lượt chia sẻ - Side by side on mobile */}
          <div className="grid grid-cols-2 gap-4 md:col-span-2 md:grid-cols-2">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="text-3xl font-bold text-[#fe6601] mb-2">
                {filteredCharityData.length}
              </div>
              <div className="text-gray-600 font-semibold">Chương trình đang triển khai</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="text-3xl font-bold text-[#fe6601] mb-2">
                {formatCount(
                  filteredCharityData.reduce((sum, item) => sum + (item.countShare || 0), 0),
                )}
              </div>
              <div className="text-gray-600 font-semibold">Tổng lượt chia sẻ</div>
            </div>
          </div>

          {/* Tổng quỹ chia sẻ - Full width on mobile */}
          <div className="bg-white rounded-lg shadow-md p-6 text-center md:col-span-1">
            <div className="text-3xl font-bold text-[#fe6601] mb-2">
              {formatAmount(
                filteredCharityData.reduce((sum, item) => sum + (item.countShare || 0) * 50000, 0),
              )}
            </div>
            <div className="text-gray-600 font-semibold">Tổng quỹ chia sẻ</div>
          </div>
        </div>

        {/* Charity Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCharityData.map((charity, index) => (
            <div
              key={charity.top}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              {/* Image */}
              <div className="relative h-64">
                <img
                  src={charity.avatarLink}
                  alt={charity.title}
                  className="w-full h-full object-cover"
                  loading={index < 3 ? 'eager' : 'lazy'}
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-[#fe6601] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    #{charity.top}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Title */}
                <h3 className="text-xl font-bold text-[#fe6601] mb-3 line-clamp-2">
                  {charity.title}
                </h3>

                {/* Description */}
                <div className="mb-4">
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line line-clamp-4">
                    {charity.description}
                  </p>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 font-semibold">Quỹ chia sẻ</span>
                    <span className="text-lg font-bold text-[#fe6601]">
                      {formatAmount(charity.countShare * 50000 || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 font-semibold">Lượt chia sẻ</span>
                    <span className="text-lg font-bold text-[#fe6601]">
                      {formatCount(charity.countShare)} lượt
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleViewMore(charity)}
                  className="w-full bg-[#fe6601] hover:bg-[#e55a00] text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 text-sm"
                >
                  Tìm hiểu thêm
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#fe6601] mb-4">
              Cùng chung tay lan tỏa yêu thương
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Mỗi lượt chia sẻ của bạn đều góp phần tạo nên sự khác biệt tích cực trong cộng đồng.
              Hãy cùng GK88 lan tỏa những thông điệp ý nghĩa này đến với nhiều người hơn.
            </p>
            <button className="bg-[#fe6601] hover:bg-[#e55a00] text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200">
              Tham gia ngay
            </button>
          </div>
        </div>
      </div>

      {/* Charity Detail Modal */}
      <CharityDetailModal charity={selectedCharity} isOpen={isModalOpen} onClose={closeModal} />
    </div>
  );
};

export default CharityPage;
